/**
 * HandoffPage — /auth/handoff?token=<accessToken>
 *
 * An enterprise user who just logged in on app.domain is redirected here on
 * their enterprise subdomain with the access token in the URL. We immediately
 * consume the token (strip it from the URL for security), re-issue a new
 * session scoped to this enterprise by hitting /auth/me, and redirect to /dashboard.
 */
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuthStore } from "../../stores/authStore";
import { apiClient } from "../../lib/apiClient";
import { appSubdomainUrl, isEnterpriseSubdomain } from "../../lib/subdomain";
import type { UserWithContext } from "@platform/types";

export function HandoffPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSession, clear } = useAuthStore();
  const consumed = useRef(false);

  useEffect(() => {
    if (consumed.current) return;
    consumed.current = true;

    const token = searchParams.get("token");

    // Strip token from URL bar immediately
    window.history.replaceState({}, "", "/auth/handoff");

    if (!token) {
      // On enterprise subdomain, always bounce back to app subdomain login
      if (isEnterpriseSubdomain()) {
        window.location.replace(appSubdomainUrl("/login"));
      } else {
        navigate("/login", { replace: true });
      }
      return;
    }

    // Seed store with the token so api-client picks it up for /me
    useAuthStore.setState({ accessToken: token });

    // Fetch /me — API uses Origin header to scope to this enterprise
    apiClient.auth.me()
      .then((user) => {
        setSession({ user: user as UserWithContext, accessToken: token, refreshToken: "" });
        navigate("/dashboard", { replace: true });
      })
      .catch(() => {
        clear();
        // Always redirect to app subdomain login — the enterprise subdomain
        // login cannot recover from a handoff failure (no session to work with)
        const loginUrl = appSubdomainUrl("/login?reason=handoff_failed");
        if (loginUrl.startsWith("http")) {
          window.location.replace(loginUrl);
        } else {
          navigate("/login?reason=handoff_failed", { replace: true });
        }
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-ds-bg">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-sm text-ds-text-secondary">Loading your workspace...</p>
      </div>
    </div>
  );
}
